import { supabase } from '../utils/supabaseClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * `/api/charge-map` resolves the caller's organisation from their user id and
 * filters every statement on it — the route uses the service-role client, so
 * that header is the tenant boundary, not RLS. `extractUserId` reads
 * `x-user-id` last, but it is the only source available to a GET or to a
 * multipart POST (auth runs before multer), so this service sends it on every
 * call rather than only where it is strictly required.
 */
const authHeaders = async (): Promise<Record<string, string>> => {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'x-api-key': 'scopingai',
    'x-user-id': session?.user?.id ?? '',
  };
};

const jsonHeaders = async (): Promise<Record<string, string>> => ({
  ...(await authHeaders()),
  'Content-Type': 'application/json',
});

export const CANONICAL_SECTIONS = ['retail', 'distribution', 'metering', 'environmental', 'other'] as const;
export type CanonicalSection = (typeof CANONICAL_SECTIONS)[number];

export const MAP_STATES = ['validated', 'passthrough', 'unvalidated', 'needs_review', 'group_member'] as const;
export type MapState = (typeof MAP_STATES)[number];

export const DEMAND_UNITS = ['kW', 'kVA'] as const;
export type DemandUnit = (typeof DEMAND_UNITS)[number];

/**
 * A row of `bv_retailer_charge_map` as `/list` returns it: every column, plus
 * `retailer_name` flattened out of the embedded `retailer(name)` join so the
 * grid never shows a uuid.
 *
 * `distributor_key`, `tariff_key` and `bill_item_key` are STORED GENERATED
 * columns. They are read-only — the route strips them from every write body,
 * which is what makes it safe to round-trip a listed row straight into `update`.
 */
export type ChargeMapRow = {
  id: string;
  org_id: string;
  retailer_id: string;
  retailer_name: string | null;
  distributor: string | null;
  tariff_code: string | null;
  source_tariff: string | null;
  bill_section: string;
  bill_section_canonical: CanonicalSection;
  bill_sub_section: string | null;
  bill_item: string;
  expected_item: string | null;
  expected_section: CanonicalSection;
  demand_unit: DemandUnit | null;
  group_key: string | null;
  map_state: MapState;
  notes: string | null;
  source_nmi: string | null;
  source_state: string | null;
  created_at: string;
  updated_at: string;
  distributor_key: string;
  tariff_key: string;
  bill_item_key: string;
};

/** A row of `bv_expected_charge_item` — the vocabulary `expected_item` points at. */
export type ExpectedChargeItemRow = {
  id: string;
  /** Carries the unit for a demand item ("Peak Demand - kVA"), as the network names the line. */
  name: string;
  section: CanonicalSection;
  validation_possible: boolean;
  unit: 'kW' | 'kVA' | null;
  /** Mappings naming this item; on the list response only. */
  usage_count?: number;
};

/** A mapping that names an expected item — what blocks its delete. */
export type ExpectedItemUsage = {
  id: string;
  retailer_name: string | null;
  bill_section: string;
  bill_item: string;
  distributor: string | null;
  tariff_code: string | null;
  map_state: string;
};

export const NAME_KINDS = ['distributor', 'retailer', 'metering'] as const;
export type NameKind = (typeof NAME_KINDS)[number];

/** An approved System Name and the other spellings that resolve to it. */
export type ApprovedName = { system_name: string; aliases: string[] };

/** A refused delete carries the mappings that refused it. */
export type VocabRemoveResponse = { success: boolean; error?: string; data?: ExpectedItemUsage[] };

export type ChargeMapFacets = {
  retailers: Array<{ id: string; name: string }>;
  distributors: string[];
  bill_sections: string[];
  sections: string[];
  map_states: string[];
  states: string[];
};

export type ListParams = {
  search?: string;
  retailer?: string;
  distributor?: string;
  map_state?: string;
  sort?: ChargeMapSortKey;
  dir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
};

/** Keys the list route accepts for `sort`; anything else falls back to retailer. */
export type ChargeMapSortKey =
  | 'retailer' | 'distributor' | 'tariff' | 'bill_section' | 'bill_item'
  | 'expected_item' | 'expected_section' | 'demand_unit' | 'map_state';
export type ListResponse<T> = { success: boolean; data?: T[]; count?: number; page?: number; limit?: number; error?: string };
export type MutateResponse<T> = { success: boolean; data?: T; error?: string };

/**
 * `sheet` is present only for the multi-sheet workbook shape, and must be
 * rendered when it is — "row 47" means nothing across a 16-sheet file. `row: 0`
 * means the problem belongs to a sheet rather than to one source line.
 */
export type BulkRowError = { row: number; sheet?: string; error: string };

export type BulkUploadResponse = {
  success: boolean;
  shape?: 'workbook' | 'flat';
  total?: number;
  upserted?: number;
  inserted?: number;
  updated?: number;
  duplicates_collapsed?: number;
  skipped?: number;
  errors?: BulkRowError[];
  error?: string;
};

export type ExportParams = { retailer?: string; distributor?: string };

/** Mirrors `UnmappedReport` in server/.../unmappedItemsService.ts. */
export type UnmappedLine = {
  bill_id: string;
  site_id: string | null;
  nmi: string | null;
  retailer: string | null;
  retailer_id: string | null;
  distributor: string | null;
  tariff: string | null;
  state: string | null;
  section: string;
  bill_item: string;
  amount: number;
  bill_from: string | null;
  bill_to: string | null;
};

export type UnmappedItem = {
  retailer: string | null;
  retailer_id: string | null;
  section: string;
  bill_item: string;
  distributors: string[];
  tariffs: string[];
  nmis: string[];
  bill_count: number;
  total_amount: number;
  last_bill_id: string;
  last_bill_to: string | null;
};

export type UnmappedReport = {
  items: UnmappedItem[];
  lines: UnmappedLine[];
  bills_scanned: number;
  retailers_without_map: Array<{ retailer: string; bill_count: number }>;
};

export const chargeMapAdminService = {
  list: async (params: ListParams = {}): Promise<ListResponse<ChargeMapRow>> => {
    try {
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      if (params.retailer) qs.set('retailer', params.retailer);
      if (params.distributor) qs.set('distributor', params.distributor);
      if (params.map_state) qs.set('map_state', params.map_state);
      if (params.sort) qs.set('sort', params.sort);
      if (params.dir) qs.set('dir', params.dir);
      qs.set('page', String(params.page ?? 1));
      qs.set('limit', String(params.limit ?? 25));
      const res = await fetch(`${API_BASE_URL}/api/charge-map/list?${qs.toString()}`, {
        headers: await authHeaders(),
      });
      return (await res.json()) as ListResponse<ChargeMapRow>;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  facets: async (): Promise<MutateResponse<ChargeMapFacets>> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/charge-map/facets`, {
        headers: await authHeaders(),
      });
      return (await res.json()) as MutateResponse<ChargeMapFacets>;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  create: async (body: Partial<ChargeMapRow>): Promise<MutateResponse<ChargeMapRow>> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/charge-map`, {
        method: 'POST',
        headers: await jsonHeaders(),
        body: JSON.stringify(body),
      });
      return (await res.json()) as MutateResponse<ChargeMapRow>;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  update: async (id: string, body: Partial<ChargeMapRow>): Promise<MutateResponse<ChargeMapRow>> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/charge-map/${id}`, {
        method: 'PUT',
        headers: await jsonHeaders(),
        body: JSON.stringify(body),
      });
      return (await res.json()) as MutateResponse<ChargeMapRow>;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  remove: async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/charge-map/${id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      return (await res.json()) as { success: boolean; error?: string };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  unmapped: async (): Promise<MutateResponse<UnmappedReport>> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/charge-map/unmapped`, { headers: await authHeaders() });
      return (await res.json()) as MutateResponse<UnmappedReport>;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  removeMany: async (ids: string[]): Promise<{ success: boolean; deleted?: number; error?: string }> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/charge-map/bulk-delete`, {
        method: 'POST',
        headers: await jsonHeaders(),
        body: JSON.stringify({ ids }),
      });
      return (await res.json()) as { success: boolean; deleted?: number; error?: string };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  /**
   * Accepts either the multi-sheet source workbook or a flat sheet matching the
   * CSV export's headers, so a download-edit-upload round trip works. The route
   * detects which; the response's `shape` says what it decided.
   */
  bulkUpload: async (file: File): Promise<BulkUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/charge-map/bulk`, {
        method: 'POST',
        // No Content-Type: the browser must set the multipart boundary itself.
        headers: await authHeaders(),
        body: formData,
      });
      return (await res.json()) as BulkUploadResponse;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Upload failed' };
    }
  },

  /**
   * The export needs the `x-user-id` header, so it cannot be a plain anchor —
   * fetch it, then hand the blob to a synthetic download.
   */
  exportCsv: async (params: ExportParams = {}): Promise<{ success: boolean; error?: string }> => {
    try {
      const qs = new URLSearchParams();
      if (params.retailer) qs.set('retailer', params.retailer);
      if (params.distributor) qs.set('distributor', params.distributor);
      const res = await fetch(`${API_BASE_URL}/api/charge-map/export?${qs.toString()}`, {
        headers: await authHeaders(),
      });

      if (!res.ok) {
        // A failure answers JSON, not CSV.
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        return { success: false, error: body?.error ?? `Export failed (${res.status})` };
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `charge-map-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Export failed' };
    }
  },

  /**
   * Shaped as `{ list, create, update, remove }` so `VocabularyTab` consumes it
   * directly, with no adapter.
   */
  vocabulary: {
    list: async (): Promise<ListResponse<ExpectedChargeItemRow>> => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/charge-map/vocabulary`, {
          headers: await authHeaders(),
        });
        return (await res.json()) as ListResponse<ExpectedChargeItemRow>;
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
      }
    },

    create: async (body: Partial<ExpectedChargeItemRow>): Promise<MutateResponse<ExpectedChargeItemRow>> => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/charge-map/vocabulary`, {
          method: 'POST',
          headers: await jsonHeaders(),
          body: JSON.stringify(body),
        });
        return (await res.json()) as MutateResponse<ExpectedChargeItemRow>;
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
      }
    },

    update: async (
      id: string,
      body: Partial<ExpectedChargeItemRow>,
    ): Promise<MutateResponse<ExpectedChargeItemRow>> => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/charge-map/vocabulary/${id}`, {
          method: 'PUT',
          headers: await jsonHeaders(),
          body: JSON.stringify(body),
        });
        return (await res.json()) as MutateResponse<ExpectedChargeItemRow>;
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
      }
    },

    remove: async (id: string): Promise<VocabRemoveResponse> => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/charge-map/vocabulary/${id}`, {
          method: 'DELETE',
          headers: await authHeaders(),
        });
        return (await res.json()) as VocabRemoveResponse;
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
      }
    },

    /** The mappings that name this item. */
    usages: async (id: string): Promise<ListResponse<ExpectedItemUsage>> => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/charge-map/vocabulary/${id}/usages`, {
          headers: await authHeaders(),
        });
        return (await res.json()) as ListResponse<ExpectedItemUsage>;
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
      }
    },
  },

  /** Approved names and their other spellings (bv_name / bv_name_alias). */
  names: {
    list: async (kind: NameKind): Promise<ListResponse<ApprovedName>> => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/charge-map/names?kind=${kind}`, {
          headers: await authHeaders(),
        });
        return (await res.json()) as ListResponse<ApprovedName>;
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
      }
    },

    create: async (kind: NameKind, systemName: string): Promise<MutateResponse<ApprovedName>> => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/charge-map/names`, {
          method: 'POST',
          headers: await jsonHeaders(),
          body: JSON.stringify({ kind, system_name: systemName }),
        });
        return (await res.json()) as MutateResponse<ApprovedName>;
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
      }
    },

    addAlias: async (kind: NameKind, alias: string, systemName: string): Promise<MutateResponse<unknown>> => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/charge-map/names/alias`, {
          method: 'POST',
          headers: await jsonHeaders(),
          body: JSON.stringify({ kind, alias, system_name: systemName }),
        });
        return (await res.json()) as MutateResponse<unknown>;
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
      }
    },

    removeAlias: async (kind: NameKind, alias: string): Promise<MutateResponse<unknown>> => {
      try {
        const qs = new URLSearchParams({ kind, alias });
        const res = await fetch(`${API_BASE_URL}/api/charge-map/names/alias?${qs.toString()}`, {
          method: 'DELETE',
          headers: await authHeaders(),
        });
        return (await res.json()) as MutateResponse<unknown>;
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
      }
    },
  },
};
